import { MapContainer, TileLayer, Marker, Popup, useMap, LayersControl } from 'react-leaflet'
import MarkerClusterGroup from 'react-leaflet-cluster'
import 'leaflet/dist/leaflet.css'
import L from 'leaflet'
import { LocalShipping, RvHookup, Inventory } from '@mui/icons-material'
import { renderToString } from 'react-dom/server'
import { AssetPosition } from 'utils/test/data-mocked/assetPositions'
import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
interface AssetMapProps {
  positions: AssetPosition[]
  selectedAsset?: AssetPosition
}

const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_KEY

const MapUpdater = ({
  positions,
  selectedAsset,
  clusterRef,
}: {
  positions: AssetPosition[]
  selectedAsset?: AssetPosition
  clusterRef: React.RefObject<any>
}) => {
  const map = useMap()

  useEffect(() => {
    if (selectedAsset?.coords) {
      // Center on the selected asset with an appropriate zoom
      map.setView(selectedAsset.coords, 17)

      // Find and expand the cluster containing the selected marker
      if (clusterRef.current) {
        const markers = clusterRef.current.getLayers()
        const targetLatLng = L.latLng(selectedAsset.coords[0], selectedAsset.coords[1])

        markers.forEach((marker: any) => {
          if (
            marker.getLatLng &&
            marker.getLatLng().lat === targetLatLng.lat &&
            marker.getLatLng().lng === targetLatLng.lng
          ) {
            const parent = marker.__parent
            if (parent) {
              parent.spiderfy()
            }
          }
        })
      }
    } else if (positions.length > 0) {
      const validPositions = positions.filter((p): p is AssetPosition & { coords: [number, number] } => !!p.coords)
      const bounds = new L.LatLngBounds(validPositions.map(p => p.coords))
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 11 })
    } else {
      map.setView([40.7046, -73.9532], 3)
    }
  }, [map, positions, selectedAsset, clusterRef])

  return null
}

const AssetMap = ({ positions, selectedAsset }: AssetMapProps) => {
  const createCustomIcon = (IconComponent: typeof LocalShipping | typeof RvHookup | typeof Inventory) => {
    const html = renderToString(
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '50%',
          padding: '4px',
          boxShadow: '0 0 4px rgba(0,0,0,0.2)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <IconComponent
          sx={{
            color: IconComponent === Inventory ? '#1976d2' : IconComponent === LocalShipping ? '#9c27b0' : '#2e7d32',
            fontSize: 32,
          }}
        />
      </div>,
    )
    return L.divIcon({
      html,
      className: 'custom-icon',
      iconSize: [40, 40],
      iconAnchor: [20, 20],
    })
  }

  const icons = {
    container: createCustomIcon(Inventory),
    truck: createCustomIcon(LocalShipping),
    trailer: createCustomIcon(RvHookup),
  }

  const markerRefs = useRef<{ [key: string]: L.Marker }>({})
  const clusterRef = useRef<any>(null)

  useEffect(() => {
    if (selectedAsset && markerRefs.current[selectedAsset.id]) {
      markerRefs.current[selectedAsset.id].openPopup()
    }
  }, [selectedAsset])

  return (
    <MapContainer
      center={positions[0]?.coords || [40.7046, -73.9532]}
      zoom={11}
      scrollWheelZoom={true}
      style={{ height: '100%', width: '100%' }}
    >
      <MapUpdater positions={positions} selectedAsset={selectedAsset} clusterRef={clusterRef} />
      <LayersControl position="topright">
        <LayersControl.BaseLayer checked name="Street">
          <TileLayer
            url={`https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}&key=${GOOGLE_MAPS_KEY}`}
            maxZoom={20}
            attribution="&copy; Google Maps"
          />
        </LayersControl.BaseLayer>
        <LayersControl.BaseLayer name="Satellite">
          <TileLayer
            url={`https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}&key=${GOOGLE_MAPS_KEY}`}
            maxZoom={20}
            attribution="&copy; Google Maps"
          />
        </LayersControl.BaseLayer>
      </LayersControl>
      <MarkerClusterGroup ref={clusterRef} spiderfyOnMaxZoom={true} zoomToBoundsOnClick={true}>
        {positions
          .filter((pos): pos is AssetPosition & { coords: [number, number] } => !!pos.coords)
          .map(position => (
            <Marker
              key={position.id}
              position={position.coords}
              icon={icons[position.asset as keyof typeof icons]}
              ref={ref => {
                if (ref) {
                  markerRefs.current[position.id] = ref
                }
              }}
              eventHandlers={{
                add: e => {
                  e.target.setOpacity(1.0)
                },
              }}
            >
              <Popup>
                <strong>{position.assetName}</strong>
                <br />
                Customer: {position.customerName}
                <br />
                Location: {position.label}
                <br />
                Type: {position.assetType}
                <br />
                Days on Site: {position.daysOnSite}
                <br />
                <Link to={`/app/assets/containers/${position.assetId}`} target="_blank">
                  View Details
                </Link>
              </Popup>
            </Marker>
          ))}
      </MarkerClusterGroup>
    </MapContainer>
  )
}

export default AssetMap
